import { PlanTypesEnum } from '@ctct/domain-layer';

/**
 * Content definition for the value-focused mWeb variant comparison table.
 * This file contains reordered features and updated tooltip descriptions
 * specifically for the value-focused mobile web experiment.
 */
export default {
  headers: {
    plans: [
      {
        id: PlanTypesEnum.Bronze,
        name: 'Lite',
        prePrice: 'Starting at',
        afterPrice: '/month*',
      },
      {
        id: PlanTypesEnum.Silver,
        name: 'Standard',
        prePrice: 'Starting at',
        afterPrice: '/month*',
      },
      {
        id: PlanTypesEnum.Gold,
        name: 'Premium',
        prePrice: 'Starting at',
        afterPrice: '/month*',
      },
    ],
  },
  groups: [
    {
      id: 'platformManagement',
      name: 'Platform Management',
      features: [
        {
          id: 'platformManagement-users',
          feature: {
            name: 'Users',
            tooltipLabel: 'Grant account access to team members to collaborate on your marketing.',
          },
          parameters: [
            { id: '1', value: '1' },
            { id: '2', value: '3' },
            { id: '3', value: true },
          ],
        },
        {
          id: 'platformManagement-monthlyEmailSends',
          feature: {
            name: 'Monthly email sends\n(based on total contacts)',
            tooltipLabel: 'Amount of emails included in your plan.',
            tooltipLinkText: 'Overage fees of $0.002 per email apply.',
            tooltipLinkUrl:
              'https://knowledgebase.constantcontact.com/email-digital-marketing/articles/KnowledgeBase/46046-Constant-Contacts-email-send-allowance-and-overage-fee?lang=en_US',
          },
          parameters: [
            { id: '1', value: '10x' },
            { id: '2', value: '12x' },
            { id: '3', value: '24x' },
          ],
        },
      ],
    },
    {
      id: 'email',
      name: 'Email',
      features: [
        {
          id: 'email-templates',
          feature: {
            name: 'Templates',
            tooltipLabel: 'Access hundreds of mobile-responsive templates for any industry or occasion.',
          },
          parameters: [
            { id: '1', value: true },
            { id: '2', value: true },
            { id: '3', value: true },
          ],
        },
        {
          id: 'email-brandKit',
          feature: {
            name: 'BrandKit',
            tooltipLabel: "Automatically pull your brand's colors and logo into your emails for a consistent look.",
          },
          parameters: [
            { id: '1', value: true },
            { id: '2', value: true },
            { id: '3', value: true },
          ],
        },
        {
          id: 'email-pollsSurveys',
          feature: {
            name: 'Polls & surveys',
            tooltipLabel: 'Gather valuable feedback and insights directly from your audience within your emails.',
          },
          parameters: [
            { id: '1', value: true },
            { id: '2', value: true },
            { id: '3', value: true },
          ],
        },
        {
          id: 'email-reachAndEngagementReporting',
          feature: {
            name: 'Reach and engagement reporting',
            tooltipLabel: 'Track opens, clicks, and sends to see how your audience interacts with your content.',
          },
          parameters: [
            { id: '1', value: true },
            { id: '2', value: true },
            { id: '3', value: true },
          ],
        },
        {
          id: 'email-aiContent',
          feature: {
            name: 'AI content',
            tooltipLabel: 'Instantly generate catchy subject lines and email copy with our built-in AI writer.',
          },
          parameters: [
            { id: '1', value: true },
            { id: '2', value: true },
            { id: '3', value: true },
          ],
        },
        {
          id: 'email-scheduledEmailSends',
          feature: {
            name: 'Schedule email sends',
            tooltipLabel: 'Plan ahead by scheduling your emails to go out at the perfect date and time.',
          },
          parameters: [
            { id: '1', value: false },
            { id: '2', value: true },
            { id: '3', value: true },
          ],
        },
        {
          id: 'email-subjectLineRecommendations',
          feature: {
            name: 'Subject line recommendation',
            tooltipLabel: 'Get AI-powered suggestions to help you write subject lines that get more opens.',
          },
          parameters: [
            { id: '1', value: false },
            { id: '2', value: true },
            { id: '3', value: true },
          ],
        },
        {
          id: 'email-subjectLineABTesting',
          feature: {
            name: 'Subject line A/B testing',
            tooltipLabel: 'Test two different subject lines to see which one performs best with your audience.',
          },
          parameters: [
            { id: '1', value: false },
            { id: '2', value: true },
            { id: '3', value: true },
          ],
        },
        {
          id: 'email-subjectLinePersonalization',
          feature: {
            name: 'Subject line personalization',
            tooltipLabel: "Increase engagement by automatically adding your contact's name to the subject line.",
          },
          parameters: [
            { id: '1', value: false },
            { id: '2', value: true },
            { id: '3', value: true },
          ],
        },
        {
          id: 'email-dynamicContentInEmail',
          feature: {
            name: 'Dynamic email content',
            tooltipLabel: 'Show different content to different people within the same email based on their interests.',
          },
          parameters: [
            { id: '1', value: false },
            { id: '2', value: false },
            { id: '3', value: true },
          ],
        },
        {
          id: 'email-drilldownReporting',
          feature: {
            name: 'Drilldown reporting',
            tooltipLabel: 'Dive deeper into your data to see exactly which contacts clicked specific links.',
          },
          parameters: [
            { id: '1', value: false },
            { id: '2', value: true },
            { id: '3', value: true },
          ],
        },
        {
          id: 'email-engagementHeatMap',
          feature: {
            name: 'Engagement heat maps',
            tooltipLabel: 'See a visual map of where your subscribers are clicking most within your emails.',
          },
          parameters: [
            { id: '1', value: false },
            { id: '2', value: false },
            { id: '3', value: true },
          ],
        },
        {
          id: 'email-newsletterArchive',
          feature: {
            name: 'Newsletter archive',
            tooltipLabel: 'Showcase your past email campaigns on your website for new visitors to explore.',
          },
          parameters: [
            { id: '1', value: false },
            { id: '2', value: true },
            { id: '3', value: true },
          ],
        },
      ],
    },
    {
      id: 'automation',
      name: 'Automations',
      features: [
        {
          id: 'automation-automationsTemplates',
          feature: {
            name: 'Automation templates',
            tooltipLabel: 'Use pre-built workflows to send welcome emails or birthday greetings automatically.',
          },
          parameters: [
            { id: '1', value: '1' },
            { id: '2', value: '3' },
            { id: '3', value: true },
          ],
        },
        {
          id: 'automation-customAutomations',
          feature: {
            name: 'Custom automations',
            tooltipLabel: 'Build tailored customer journeys that trigger emails based on specific contact actions.',
          },
          parameters: [
            { id: '1', value: false },
            { id: '2', value: false },
            { id: '3', value: true },
          ],
        },
        {
          id: 'automation-ecommerceAutomationTemplates',
          feature: {
            name: 'Ecommerce automation templates',
            tooltipLabel: 'Drive sales with automated abandoned cart reminders and post-purchase follow-ups.',
          },
          parameters: [
            { id: '1', value: false },
            { id: '2', value: false },
            { id: '3', value: true },
          ],
        },
        {
          id: 'automation-resendToNonOpeners',
          feature: {
            name: 'Resend to non-openers',
            tooltipLabel:
              'Automatically resend your email to contacts who missed it the first time with a new subject line.',
          },
          parameters: [
            { id: '1', value: false },
            { id: '2', value: true },
            { id: '3', value: true },
          ],
        },
      ],
    },
    {
      id: 'socialMarketing',
      name: 'Social Marketing',
      features: [
        {
          id: 'socialMarketing-socialPosts',
          feature: {
            name: 'Social posts',
            tooltipLabel: 'Create and publish updates to Facebook, Instagram, LinkedIn, and TikTok.',
          },
          parameters: [
            { id: '1', value: true },
            { id: '2', value: true },
            { id: '3', value: true },
          ],
        },
        {
          id: 'socialMarketing-postShortVideo',
          feature: {
            name: 'Post short videos',
            tooltipLabel: 'Engage your followers by sharing Reels and TikTok videos directly through your account.',
          },
          parameters: [
            { id: '1', value: '5' },
            { id: '2', value: true },
            { id: '3', value: true },
          ],
        },
        {
          id: 'socialMarketing-socialReporting',
          feature: {
            name: 'Social reporting',
            tooltipLabel: 'See how your social posts are performing with data on reach, likes, and shares.',
          },
          parameters: [
            { id: '1', value: true },
            { id: '2', value: true },
            { id: '3', value: true },
          ],
        },
        {
          id: 'socialMarketing-schedulePostsInAdvance',
          feature: {
            name: 'Schedule posts in advance',
            tooltipLabel: 'Save time by planning and scheduling your social media content weeks or months ahead.',
          },
          parameters: [
            { id: '1', value: false },
            { id: '2', value: true },
            { id: '3', value: true },
          ],
        },
      ],
    },
    {
      id: 'smsMarketing',
      name: 'SMS Marketing',
      features: [
        {
          id: 'smsMarketing-smsCampaigns',
          feature: {
            name: 'SMS campaigns',
            tooltipLabel:
              "Send time-sensitive text messages directly to your customers' mobile phones. Visit SMS add-on on desktop to see pricing.",
          },
          parameters: [
            { id: '1', value: 'Add-on' },
            { id: '2', value: 'Add-on' },
            { id: '3', value: '500/mo*' },
          ],
        },
        {
          id: 'smsMarketing-textToJoin',
          feature: {
            name: 'Text-to-join',
            tooltipLabel: 'Grow your list by allowing people to sign up for your marketing via a simple text keyword.',
          },
          parameters: [
            { id: '1', value: 'Add-on' },
            { id: '2', value: 'Add-on' },
            { id: '3', value: true },
          ],
        },
        {
          id: 'smsMarketing-smsReporting',
          feature: {
            name: 'SMS reporting',
            tooltipLabel: 'Track the success of your text campaigns with data on delivery and click rates.',
          },
          parameters: [
            { id: '1', value: 'Add-on' },
            { id: '2', value: 'Add-on' },
            { id: '3', value: true },
          ],
        },
      ],
      disclaimer: '*Standard SMS rates apply after your first 500 messages.',
      disclaimerLinkText: 'Standard SMS rates apply',
      disclaimerLinkUrl:
        'https://knowledgebase.constantcontact.com/email-digital-marketing/articles/KnowledgeBase/46517-Add-the-SMS-marketing-add-on-to-your-Constant-Contact-email-plan?lang=en_US',
    },
    {
      id: 'marketingCrm',
      name: 'Marketing CRM',
      features: [
        {
          id: 'marketingCrm-contactSegmentation',
          feature: {
            name: 'Contact segmentation',
            tooltipLabel: 'Group your contacts based on interests or demographics to send more relevant content.',
          },
          parameters: [
            { id: '1', value: true },
            { id: '2', value: true },
            { id: '3', value: true },
          ],
        },
        {
          id: 'marketingCrm-webSignUpForms',
          feature: {
            name: 'Web sign-up forms',
            tooltipLabel: 'Add customizable forms to your website to capture new leads and grow your list.',
          },
          parameters: [
            { id: '1', value: true },
            { id: '2', value: true },
            { id: '3', value: true },
          ],
        },
        {
          id: 'marketingCrm-contactManagement',
          feature: {
            name: 'Contact management',
            tooltipLabel: 'Organize, upload, and manage all your subscriber information in one secure location.',
          },
          parameters: [
            { id: '1', value: true },
            { id: '2', value: true },
            { id: '3', value: true },
          ],
        },
        {
          id: 'marketingCrm-engagementSegmentation',
          feature: {
            name: 'Engagement segmentation',
            tooltipLabel: 'Create segments based on how often contacts open or click your emails.',
          },
          parameters: [
            { id: '1', value: false },
            { id: '2', value: true },
            { id: '3', value: true },
          ],
        },
        {
          id: 'marketingCrm-ecommerceSegmentation',
          feature: {
            name: 'Ecommerce segmentation',
            tooltipLabel: 'Target customers based on their past purchase behavior and shopping habits.',
          },
          parameters: [
            { id: '1', value: false },
            { id: '2', value: false },
            { id: '3', value: true },
          ],
        },
      ],
    },
    {
      id: 'ads',
      name: 'Ads',
      features: [
        {
          id: 'ads-socialMediaAdsManager',
          feature: {
            name: 'Social media ads manager',
            tooltipLabel: 'Create and manage paid ad campaigns for Facebook and Instagram to reach new audiences.',
          },
          parameters: [
            { id: '1', value: false },
            { id: '2', value: true },
            { id: '3', value: true },
          ],
        },
        {
          id: 'ads-facebookLookalikeAdTargeting',
          feature: {
            name: 'Facebook Lookalike Ad targeting',
            tooltipLabel: "Find new leads who are similar to your best existing customers using Facebook's data.",
          },
          parameters: [
            { id: '1', value: false },
            { id: '2', value: false },
            { id: '3', value: true },
          ],
        },
        {
          id: 'ads-googleAdsManager',
          feature: {
            name: 'Google Ads Manager',
            tooltipLabel:
              'Launch and track Google search ads to get your business in front of people searching for you.',
          },
          parameters: [
            { id: '1', value: false },
            { id: '2', value: false },
            { id: '3', value: true },
          ],
        },
        {
          id: 'ads-googleAdsCredit',
          feature: {
            name: '$500 Google Ads credit',
            tooltipLabel:
              'Spend $500 on Google Ads in your first 60 days and get an additional $500 in ad credit. New US accounts only.',
            tooltipLinkText: 'Terms and conditions apply',
            tooltipLinkUrl: 'https://www.google.com/ads/coupons/terms/',
          },
          parameters: [
            { id: '1', value: false },
            { id: '2', value: false },
            { id: '3', value: true },
          ],
        },
      ],
    },
    {
      id: 'events',
      name: 'Events',
      features: [
        {
          id: 'events-sellProductsServices',
          feature: {
            name: 'Sell products & services',
            tooltipLabel: 'Create a professional landing page to sell items or services directly to your audience.',
          },
          parameters: [
            { id: '1', value: true },
            { id: '2', value: true },
            { id: '3', value: true },
          ],
        },
        {
          id: 'events-payments',
          feature: {
            name: 'Payments',
            tooltipLabel: 'Securely collect payments for tickets or products through Stripe or PayPal integrations.',
          },
          parameters: [
            { id: '1', value: true },
            { id: '2', value: true },
            { id: '3', value: true },
          ],
        },
        {
          id: 'events-registrationForm',
          feature: {
            name: 'Registration form',
            tooltipLabel: 'Build custom forms to collect attendee information for your events or webinars.',
          },
          parameters: [
            { id: '1', value: true },
            { id: '2', value: true },
            { id: '3', value: true },
          ],
        },
      ],
    },
    {
      id: 'additionalFeatures',
      name: 'Additional Features',
      features: [
        {
          id: 'additionalFeatures-marketingCampaignCalendar',
          feature: {
            name: 'Marketing campaign calendar',
            tooltipLabel: 'View and manage all your scheduled emails, social posts, and events in one visual view.',
          },
          parameters: [
            { id: '1', value: true },
            { id: '2', value: true },
            { id: '3', value: true },
          ],
        },
        {
          id: 'additionalFeatures-300Integrations',
          feature: {
            name: '300+ integrations',
            tooltipLabel:
              'Connect your favorite tools like Shopify, Canva, and Salesforce to streamline your workflow.',
          },
          parameters: [
            { id: '1', value: true },
            { id: '2', value: true },
            { id: '3', value: true },
          ],
        },
        {
          id: 'additionalFeatures-campaignBuilder',
          feature: {
            name: 'Campaign Builder',
            tooltipLabel: 'Use a step-by-step guide to create cohesive marketing campaigns across multiple channels.',
          },
          parameters: [
            { id: '1', value: false },
            { id: '2', value: true },
            { id: '3', value: true },
          ],
        },
        {
          id: 'additionalFeatures-seoRecommendations',
          feature: {
            name: 'SEO recommendations',
            tooltipLabel: "Improve your website's visibility on search engines with personalized ranking tips.",
          },
          parameters: [
            { id: '1', value: false },
            { id: '2', value: false },
            { id: '3', value: true },
          ],
        },
        {
          id: 'additionalFeatures-revenueReporting',
          feature: {
            name: 'Revenue reporting',
            tooltipLabel:
              'See exactly how much money your email and social campaigns are generating for your business.',
          },
          parameters: [
            { id: '1', value: false },
            { id: '2', value: false },
            { id: '3', value: true },
          ],
        },
        {
          id: 'additionalFeatures-inboxPreview',
          feature: {
            name: 'Inbox preview',
            tooltipLabel:
              'See exactly how your email will look on different devices and email clients before you send.',
          },
          parameters: [
            { id: '1', value: 'Add-on' },
            { id: '2', value: 'Add-on' },
            { id: '3', value: 'Add-on' },
          ],
        },
      ],
    },
  ],
  marketingData: [
    {
      id: 'custom-plans',
      name: 'Plans',
      features: [
        {
          id: 'custom-plans',
          feature: { name: 'Plans', tooltipLabel: '' },
          parameters: [
            { id: '1', value: 'Lite' },
            { id: '2', value: 'Standard' },
            { id: '3', value: 'Premium' },
          ],
        },
        {
          id: 'custom-onboarding',
          feature: {
            name: 'Onboarding',
            tooltipLabel: "You'll speak 1:1 with our experts who will get you up and running in no time!",
          },
          parameters: [
            { id: '1', value: 'Live 1:1 onboarding with experts to help you get started fast' },
            { id: '2', value: 'Same as Lite' },
            { id: '3', value: 'Priority onboarding by dedicated team' },
          ],
        },
        {
          id: 'custom-liveSupport',
          feature: {
            name: 'Live Support',
            tooltipLabel:
              'An award-winning customer support team is readily available to help customers across all plans. Premium customers receive priority support with experienced agents.',
          },
          parameters: [
            { id: '1', value: 'Live phone and chat with marketing experts' },
            { id: '2', value: 'Same as Lite' },
            { id: '3', value: 'Dedicated priority support team' },
          ],
        },
        {
          id: 'custom-email',
          feature: {
            name: 'Email',
            tooltipLabel: 'Choose from hundreds of professionally designed customizable templates or create your own.',
          },
          parameters: [
            { id: '1', value: 'Easy to use drag-and-drop email editor, AI copy generator, and email templates' },
            { id: '2', value: 'Email scheduling and subject line testing' },
            { id: '3', value: 'Same as Standard, plus dynamic content' },
          ],
        },
        {
          id: 'custom-socialMediaMarketing',
          feature: {
            name: 'Social Media Marketing',
            tooltipLabel:
              'Start posting in minutes  — all in one place. Schedule content in a few clicks, reach new audiences with social posts, and turn followers into contacts.',
          },
          parameters: [
            { id: '1', value: 'Automatic posts on Facebook, Instagram, LinkedIn, and others to find new customers' },
            { id: '2', value: 'Schedule all social media posts in advance' },
            { id: '3', value: 'Same as Standard' },
          ],
        },
        {
          id: 'custom-reporting',
          feature: {
            name: 'Reporting',
            tooltipLabel: "See what's working and what's not with the ability to track your success in real time.",
          },
          parameters: [
            { id: '1', value: "Basic: See what worked and what didn't" },
            { id: '2', value: 'Advanced: See who opens and clicks on emails' },
            { id: '3', value: 'Same as Standard' },
          ],
        },
        {
          id: 'custom-automation',
          feature: {
            name: 'Automation',
            tooltipLabel:
              'Set up easy automations to make sure your emails and texts go to the right person at the right time.',
          },
          parameters: [
            { id: '1', value: '1 automation template' },
            { id: '2', value: '3 automation templates, AI campaign builder, and automatic resend to non-openers' },
            {
              id: '3',
              value:
                'Same as Standard, plus unlimited templates, custom automations, ecommerce templates, auto-send welcome emails',
            },
          ],
        },
        {
          id: 'custom-customerRelationshipManagement',
          feature: {
            name: 'Customer Relationship Management',
            tooltipLabel:
              'Add and manage contacts in your account. You can manually add, import a list, or create an automatic way a contact gets added to your list. Every email includes a SafeUnsubscribe® link assuring your customers that you have used a legitimate permission-based email provider.',
          },
          parameters: [
            { id: '1', value: 'Easy contact management, web signup forms, and 1 custom segment' },
            { id: '2', value: 'Same as Lite with 10 custom segments' },
            { id: '3', value: 'Same as Lite with unlimited custom segments' },
          ],
        },
        {
          id: 'custom-advertising',
          feature: {
            name: 'Advertising',
            tooltipLabel:
              'Boost awareness and traffic for your business with targeted display ads in Facebook and/or Instagram feeds.',
          },
          parameters: [
            { id: '1', value: false },
            { id: '2', value: 'Powerful social media ads manager' },
            {
              id: '3',
              value: 'Same as Standard, plus Facebook lookalike targeting, Google Ad Manager, and SEO recommendations',
            },
          ],
        },
        {
          id: 'custom-events',
          feature: {
            name: 'Events',
            tooltipLabel:
              'Create, promote, and manage your event, all in one place. Enable tickets, add promo codes, and set up payment methods as part of the registration and payment process.',
          },
          parameters: [
            { id: '1', value: 'Host events, sell products, and take payments' },
            { id: '2', value: 'Host events, sell products, and take payments' },
            { id: '3', value: 'Host events, sell products, and take payments' },
          ],
        },
        {
          id: 'custom-integratedApps',
          feature: {
            name: 'Integrated Apps',
            tooltipLabel:
              'With over 300+ integrations, easily add your favorite apps to Constant Contact and sync data, simplify workflows, and amplify your marketing impact.',
          },
          parameters: [
            {
              id: '1',
              value:
                'Import your data using 300 integrations, including Google, Microsoft, Facebook, Canva, LinkedIn, Vimeo, Zapier, Shopify, Eventbrite and more!',
            },
            {
              id: '2',
              value:
                'Import your data using 300 integrations, including Google, Microsoft, Facebook, Canva, LinkedIn, Vimeo, Zapier, Shopify, Eventbrite and more!',
            },
            {
              id: '3',
              value:
                'Import your data using 300 integrations, including Google, Microsoft, Facebook, Canva, LinkedIn, Vimeo, Zapier, Shopify, Eventbrite and more!',
            },
          ],
        },
      ].map((feature) => ({
        ...feature,
        parameters: [...feature.parameters].reverse(),
      })),
    },
  ],
};
